import { GetThemeQueryResponse, Theme } from 'app/data/types/theme';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { useApi } from 'app/shared/contexts/api/api.context';
import { useBuilding } from 'app/shared/contexts/building';
import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { useEffect } from 'react';
import { useErrors } from 'app/shared/contexts/errors';
import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';

export const THEME_QUERY_KEY = 'theme';

export const getUseThemeQueryKey = (buildingUuid: string): QueryKey => [THEME_QUERY_KEY, buildingUuid];

export function useThemeQuery(sdkTheme?: Theme | null): GetThemeQueryResponse {
  const { addGenericError, hideGenericErrors } = useErrors();
  const { getAuthHeaders, getDefaultHeaders, apiUrl } = useApi();
  const { building } = useBuilding();

  const getThemeRequest = async (): Promise<Theme | null> => {
    const headers = await getAuthHeaders();
    const requestHeaders = { ...headers, ...getDefaultHeaders() };

    const response = await apiRequest<Theme, void, void>({
      url: `${apiUrl}/api/buildings/${building?.uuid}/theme`,
      method: HttpMethod.GET,
      headers: requestHeaders,
    });

    if (response && !response.isEmpty()) {
      return response.data;
    }

    return null;
  };

  const { data, error, isLoading, isSuccess } = useQuery<Theme | null, Error>({
    queryKey: getUseThemeQueryKey(building?.uuid ?? ''),
    queryFn: getThemeRequest,
    enabled: !!building?.uuid && !sdkTheme,
    retry: false,
  });

  useEffect(() => {
    if (error) {
      addGenericError(THEME_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideGenericErrors(THEME_QUERY_KEY);
    }
  }, [error, addGenericError, hideGenericErrors]);

  return {
    theme: sdkTheme ?? data ?? null,
    error: error ? { error, errorCode: ERROR_DEFAULT } : null,
    isLoading,
    isSuccess,
  };
}
